import { useEffect, useRef, useState } from "react";
import { anyType } from "../../types";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { drawStyle } from '../../lib/mapboxdraw-styles';

// Mapbox Draw を MapLibre に対応させるために必要
MapboxDraw.constants.classes.CONTROL_BASE = 'maplibregl-ctrl' as 'mapboxgl-ctrl';
MapboxDraw.constants.classes.CONTROL_PREFIX = 'maplibregl-ctrl-' as 'mapboxgl-ctrl-';
MapboxDraw.constants.classes.CONTROL_GROUP = 'maplibregl-ctrl-group' as 'mapboxgl-ctrl-group';
MapboxDraw.constants.classes.ATTRIBUTION = 'maplibregl-ctrl-attrib' as 'mapboxgl-ctrl-attrib';


export const useDrawControl = (map: maplibregl.Map | null, color: {r:number, g:number, b:number, a:number}, isOpen: boolean) => {

    const mapBoxDraw = useRef<anyType | undefined>(undefined);
    const [drawFeatureId, setDrawFeatureId] = useState<string>('');    // 作図中のFeature ID

    // 作図時のFeature IDを取得
    const setDrawFeature = (e: anyType) => {
        if(e.features.length === 0) { return; }
        setDrawFeatureId(e.features[0].id);
    };

    useEffect(() => {

        // mapbox drawの初期化
        mapBoxDraw.current = new MapboxDraw({
            displayControlsDefault: false,
            userProperties: true,
            controls: {
                point: true,
                line_string: true,
                polygon: true,
                trash: true
            },
            styles: drawStyle
        });

        return () => {
            mapBoxDraw.current = undefined;
        }
    }, []);


    /* ****************
    * 作図の切り替え
    * ****************/
    useEffect(() => {
        if (!map || !mapBoxDraw.current) return;
        
        if (isOpen) {
            map.addControl(mapBoxDraw.current, 'top-right');
            map.on('draw.create', setDrawFeature);
            map.on('draw.update', setDrawFeature);
            map.on('draw.selectionchange', setDrawFeature);
        }

        return () => {
            if(Object.keys(map._listeners).includes('draw.create')) { map.off('draw.create', setDrawFeature); }
            if(Object.keys(map._listeners).includes('draw.update')) { map.off('draw.update', setDrawFeature); }
            if(Object.keys(map._listeners).includes('draw.selectionchange')) { map.off('draw.selectionchange', setDrawFeature); }
            if(map._controls.includes(mapBoxDraw.current)) { map.removeControl(mapBoxDraw.current); }
        }
    }, [isOpen, map]);


    /* ****************
     * 作図中の地物の色を変更
     * ****************/
    useEffect(() => {
        if (!drawFeatureId || !mapBoxDraw.current || drawFeatureId === '' || !map) { return; }

        if (!mapBoxDraw.current) { return; }

        const drawFeature = mapBoxDraw.current.get(drawFeatureId);

        if (!drawFeature) { return; }

        const rgba = `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;

        mapBoxDraw.current.setFeatureProperty(drawFeatureId, 'marker-color', rgba)
            .setFeatureProperty(drawFeatureId, 'stroke', rgba)
            .setFeatureProperty(drawFeatureId, 'fill', rgba);

        const feature = mapBoxDraw.current.get(drawFeatureId);
        if (!feature) { return; }
        mapBoxDraw.current.add(feature);

    }, [color, drawFeatureId, map]);

};
